<template>
  <v-expansion-panel
    class="mb-5 custom-panel"
    :class="{ 'disabled-panel': disabled }"
  >
    <v-expansion-panel-header
      :class="'step-header-' + stepNumber"
      :disabled="disabled"
      @click="handleHeaderClick"
    >
      <div
        class="d-flex w-100"
        :class="{ 'align-center': !isMobileBreakpoint }"
      >
        <div v-if="loading" class="nalps"></div>
        <span class="step-circle" v-else-if="!completed">{{ stepNumber }}</span>
        <v-icon
          v-if="completed"
          color="success"
          size="20"
          :class="{ 'd-flex align-start': isMobileBreakpoint }"
        >
          mdi-check-circle
        </v-icon>
        <span
          class="body-regular-small neutral-30-base--text step-title"
          :class="[isMobileBreakpoint ? 'ml-2' : 'ml-5']"
        >
          <slot name="title" />
        </span>
      </div>
    </v-expansion-panel-header>
    <v-expansion-panel-content
      :class="[
        'text-left',
        isMobileBreakpoint ? 'pl-0' : 'pl-11',
        'step-content-' + stepNumber
      ]"
    >
      <slot name="content" />
      <div
        :class="[
          'step-panel-actions',
          { 'final-step-actions': isFinalStep, mobile: isMobileBreakpoint },
        ]"
        v-if="showActions"
      >
        <slot name="actions">
          <template v-if="isFinalStep">
            <base-btn
              class="mr-2"
              size="small"
              :style="
                isMobileBreakpoint
                  ? 'width: 100%; margin-bottom: 12px;'
                  : 'width: auto;'
              "
              @click="$emit('openIntegration')"
            >
              {{ $t('message.pages.drm.openIntegration') }}
            </base-btn>
            <base-btn
              type="secondary"
              size="small"
              :style="isMobileBreakpoint ? 'width: 100%;' : 'width: auto;'"
              @click="$emit('backToList')"
            >
              {{ $t('message.pages.drm.backToList') }}
            </base-btn>
          </template>
          <template v-else>
            <base-btn
              size="small"
              @click="$emit('nextStep')"
              :style="{ width: isMobileBreakpoint ? '100%' : 'auto' }"
              :disabled="nextLoading || nextDisabled"
              :loading="nextLoading"
            >
              {{
                isMobileBreakpoint
                  ? $t('message.actions.next')
                  : $t('message.actions.nextStep')
              }}
            </base-btn>
          </template>
        </slot>
      </div>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'StepPanel',
  props: {
    stepNumber: Number,
    completed: Boolean,
    disabled: Boolean,
    showActions: {
      type: Boolean,
      default: true,
    },
    actionClass: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    nextLoading: {
      type: Boolean,
      default: false,
    },
    nextDisabled: {
      type: Boolean,
      default: false,
    },
    isFinalStep: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
  },
  methods: {
    handleHeaderClick() {
      this.$emit('headerClick', this.stepNumber - 1)
    },
  },
}
</script>

<style scoped lang="scss">
::v-deep .v-expansion-panel-header__icon i {
  margin: 2px;
  font-size: 20px;
  color: varFigma(neutral-20-dark);
  border-radius: var(--spacing-03, 8px);
}

.step-circle {
  display: flex;
  height: 20px;
  padding: 0px 8px;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-02, 4px);
  border-radius: var(--spacing-05, 16px);
  background: varFigma(brand-90);
  font-size: 12px;
  font-weight: 500;
  color: varFigma(brand-20-darkest);
}

.custom-panel {
  border-radius: var(--spacing-03, 8px);
  border: 1px solid varFigma(neutral-95-lightest);
  background: varFigma(neutral-100-white);
  margin-bottom: 16px;
  padding: 11px 3px;

  &:hover {
    background-color: varFigma(brand-99-lightest);
    border: 1px solid varFigma(brand-80-light);
  }

  ::v-deep .v-expansion-panel-header__icon:hover {
    border-radius: var(--spacing-02, 4px);
    background: varFigma(neutral-90);
  }
}

.disabled-panel {
  border: 1px solid varFigma(neutral-95-lightest) !important;
  background-color: varFigma(neutral-99) !important;
  cursor: default !important;

  .v-expansion-panel-header {
    cursor: default !important;
  }

  .step-circle {
    background: varFigma(neutral-95-lightest) !important;
    color: varFigma(neutral-70) !important;
  }

  .step-title {
    color: varFigma(neutral-50) !important;
  }

  ::v-deep .v-expansion-panel-header__icon {
    cursor: not-allowed !important;

    &:hover {
      background: transparent !important;
      box-shadow: none !important;
    }

    i {
      background: varFigma(neutral-100-white) !important;
      border-radius: var(--spacing-02, 4px);
      color: varFigma(neutral-70) !important;
      width: 24px;
      height: 24px;
    }
  }
}

.v-expansion-panel--active {
  background-color: varFigma(brand-99-lightest) !important;
  border: 1px solid varFigma(brand-80-light) !important;

  ::v-deep .v-expansion-panel-header__icon {
    background: varFigma(neutral-90) !important;
    border-radius: var(--spacing-02, 4px);
    color: varFigma(neutral-20-dark) !important;
    margin-top: 2px;
  }

  ::v-deep .v-expansion-panel-header {
    align-items: flex-start;
  }
}

.step-panel-actions {
  display: flex;
  gap: 12px;

  &.mobile {
    flex-direction: column;
    .base-btn {
      width: 100% !important;
      margin-bottom: 0 !important;
    }
  }
}

.nalps {
  width: 20px;
  height: 20px;
  border: 2px solid varFigma(neutral-80-lighter);
  border-radius: 50%;
  border-top: 2px solid varFigma(brand-60-base);
  -webkit-animation: spin 2s linear infinite;
  animation: spin 2s linear infinite;
}

@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  ::v-deep {
    .v-expansion-panel-content__wrap,
    .v-expansion-panel-header {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }
  }

  .custom-panel {
    padding: 0 !important;
  }
}
</style>
